import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse, ITableFilters } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

const paymentReminderApi = createApi({
  reducerPath: 'paymentReminder',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'PaymentReminderFiles',
    'PaymentReminderAgingSummary',
    'PaymentReminderInvoices',
    'PaymentReminderRemarks',
    'PaymentReminderMailLogs',
    'PaymentReminderBankDetails',
    'PaymentReminderCurrencyValues',
  ],
  endpoints: builder => ({
    // Upload Payment Reminder Excel
    uploadPaymentReminderFile: builder.mutation<
      BackendResponse<{
        fileId: number;
        requestNo: string;
        totalRecords: number;
        validRecords: number;
        invalidRecords: number;
      }>,
      FormData
    >({
      query: formData => ({
        url: '/payment-reminder/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['PaymentReminderFiles'],
    }),

    // Get Uploaded Files
    getPaymentReminderFiles: builder.query<
      {
        data: any[];
        meta: {
          page: number;
          pageSize: number;
          totalRecords: number;
          totalPages: number;
        };
      },
      {
        page?: number;
        pageSize?: number;
        search?: string;
        uploadedById?: string;
        fileType?: string;
        uploadedOnFrom?: string;
        uploadedOnTo?: string;
        customerNumber?: string;
        customerName?: string;
        sortBy?: string;
      }
    >({
      query: params => ({
        url: `/payment-reminder/files${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['PaymentReminderFiles'],
    }),

    // Get Aging Summary (Customer Buckets)
    getPaymentReminderAgingSummary: builder.query<
      {
        file: {
          fileId: number;
          requestNo: string;
          fileName: string;
          uploadedAt: string;
          updatedAt: string;
          updatedBy: string | null;
          audit: {
            uploadedBy: {
              id: number;
              name: string;
              email: string;
            } | null;
            updatedBy: {
              id: number;
              name: string;
              email: string;
            } | null;
          };
        };
        buckets: {
          bucketCode: string;
          bucketLabel: string;
          sortOrder: number;
        }[];
        customers: {
          customerId: number;
          customerNumber: string;
          customerName: string;
          customerEmail: string;
          ccEmails: string[] | null;
          aging: Record<string, number>;
          totalInvoiceAmount: number;
          totalInvoiceAmountInr?: number;
          lastMailSentAt: string | null;
          recentRemark: {
            id: number;
            remarks: string;
            createdAt: string;
          } | null;
        }[];
        meta: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      },
      {
        fileId: number;
        search?: string;
        sortOrder?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ fileId, search, sortOrder, page, limit }) => ({
        url: `/payment-reminder/files/${fileId}/aging-summary${parseQueryParams(
          {
            search,
            sortOrder,
            page,
            limit,
          },
        )}`,
        method: 'GET',
      }),
      providesTags: (result, error, { fileId }) => [
        { type: 'PaymentReminderAgingSummary', id: fileId },
      ],
    }),

    // Get Customer Invoices
    getCustomerInvoices: builder.query<
      BackendResponse<{
        list: {
          id: number;
          fileId: number;
          sapDocumentNumber: string;
          invoiceNumber: string;
          invoiceDate: string;
          dueDate: string;
          invoiceAmount: string | number;
          invoiceAmountInr?: string | number | null;
          currency: string | null;
          createdAt: string;
          customer: {
            id: number;
            customerNumber: string;
            customerName: string;
            customerEmail: string;
          };
        }[];
        count: number;
      }>,
      { fileId: number; customerId: number; page?: number; pageSize?: number }
    >({
      query: ({ fileId, customerId, page, pageSize }) => ({
        url: `/payment-reminder/files/${fileId}/customers/${customerId}/invoices${parseQueryParams(
          {
            page,
            pageSize,
          },
        )}`,
        method: 'GET',
      }),
      providesTags: (result, error, { fileId, customerId }) => [
        { type: 'PaymentReminderInvoices', id: `${fileId}-${customerId}` },
      ],
    }),

    // Get Customers for Dropdown
    getCustomersForDropdown: builder.query<
      BackendResponse<
        {
          id: number;
          customerNumber: string;
          customerName: string;
          label: string;
        }[]
      >,
      { fileId: number }
    >({
      query: ({ fileId }) => ({
        url: `/payment-reminder/files/${fileId}/customers`,
        method: 'GET',
      }),
    }),

    // Get Bucket Invoice Details
    getBucketInvoiceDetails: builder.query<
      {
        customer: {
          customerId: number;
          customerNumber: string;
          customerName: string;
          customerEmail: string;
        };
        bucket: {
          bucketCode: string;
          bucketLabel: string;
          fromDays: number;
          toDays: number | null;
        };
        invoices: {
          id: number;
          invoiceNumber: string;
          invoiceDate: string;
          dueDate: string;
          invoiceAmount: number;
          currency: string | null;
          sapDocumentNumber: string;
          status: string;
          statusLabel: string;
          statusColor: string;
          agingDays: number;
        }[];
        totals: {
          totalInvoiceAmount: number;
          invoiceCount: number;
        };
        meta: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      },
      {
        fileId: number;
        customerId: number;
        bucketCode: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ fileId, customerId, bucketCode, page, limit }) => ({
        url: `/payment-reminder/files/${fileId}/customers/${customerId}/bucket/${bucketCode}/invoices${parseQueryParams(
          {
            page,
            limit,
          },
        )}`,
        method: 'GET',
      }),
      transformResponse: (
        response: BackendResponse<{
          customer: {
            customerId: number;
            customerNumber: string;
            customerName: string;
            customerEmail: string;
          };
          bucket: {
            bucketCode: string;
            bucketLabel: string;
            fromDays: number;
            toDays: number | null;
          };
          invoices: {
            id: number;
            invoiceNumber: string;
            invoiceDate: string;
            dueDate: string;
            invoiceAmount: number;
            currency: string | null;
            sapDocumentNumber: string;
            status: string;
            statusLabel: string;
            statusColor: string;
            agingDays: number;
          }[];
          totals: {
            totalInvoiceAmount: number;
            invoiceCount: number;
          };
          meta: {
            total: number;
            page: number;
            pageSize: number;
            totalPages: number;
          };
          count: number;
        }>,
      ) => {
        // Extract data from BackendResponse and map pageSize to limit
        return {
          ...response.data,
          meta: {
            ...response.data.meta,
            limit: response.data.meta.pageSize,
          },
        };
      },
      providesTags: (result, error, { fileId, customerId, bucketCode }) => [
        {
          type: 'PaymentReminderInvoices',
          id: `bucket-${fileId}-${customerId}-${bucketCode}`,
        },
      ],
    }),

    // Get All Invoices for a File
    getAllInvoices: builder.query<
      {
        message: string;
        data: {
          id: number;
          fileId: number;
          customerId: number;
          sapDocumentNumber: string;
          invoiceNumber: string;
          invoiceDate: string;
          dueDate: string;
          invoiceAmount: string | number;
          invoiceAmountInr?: string | number | null;
          currency: string | null;
          createdAt: string;
          customerNumber: string;
          customer: {
            customerName: string;
            customerEmail: string;
            customerNumber: string;
          };
          status: string;
          statusLabel: string;
          statusColor: string;
          agingDays: number;
        }[];
        meta: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      },
      {
        fileId: number;
        page?: number;
        limit?: number;
        search?: string;
        sortBy?: string;
        customerId?: number;
        dueDateFrom?: string;
        dueDateTo?: string;
        invoiceDateFrom?: string;
        invoiceDateTo?: string;
      }
    >({
      query: ({
        fileId,
        page,
        limit,
        search,
        sortBy,
        customerId,
        dueDateFrom,
        dueDateTo,
        invoiceDateFrom,
        invoiceDateTo,
      }) => ({
        url: `/payment-reminder/files/${fileId}/invoices${parseQueryParams({
          page,
          limit,
          search,
          sortBy,
          customerId,
          dueDateFrom,
          dueDateTo,
          invoiceDateFrom,
          invoiceDateTo,
        })}`,
        method: 'GET',
      }),
      providesTags: (result, error, { fileId }) => [
        { type: 'PaymentReminderInvoices', id: `all-${fileId}` },
      ],
    }),

    // Send Payment Reminder Email
    sendPaymentReminderEmail: builder.mutation<
      BackendResponse<{ message: string }>,
      { fileId: number; customerId: number }
    >({
      query: ({ fileId, customerId }) => ({
        url: `/payment-reminder/send/${fileId}/${customerId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { fileId, customerId }) => [
        { type: 'PaymentReminderMailLogs', id: `${fileId}-${customerId}` },
        { type: 'PaymentReminderAgingSummary', id: fileId },
      ],
    }),

    // Bulk Send Payment Reminder Email
    bulkSendPaymentReminderEmail: builder.mutation<
      BackendResponse<{ message: string }>,
      { fileId: number; customerIds: number[] }
    >({
      query: ({ fileId, customerIds }) => ({
        url: `/payment-reminder/bulk-send/${fileId}`,
        method: 'POST',
        body: { customerIds },
      }),
      invalidatesTags: (result, error, { fileId }) => [
        { type: 'PaymentReminderMailLogs', id: `all-${fileId}` },
        'PaymentReminderMailLogs',
        { type: 'PaymentReminderAgingSummary', id: fileId },
      ],
    }),

    // Get Mail Logs
    getMailLogs: builder.query<
      {
        message: string;
        data: {
          id: number;
          fileId: number;
          customerId: number;
          customerNumber: string;
          customerEmail: string;
          ccEmails: string[] | null;
          subject: string;
          status: string;
          sentAt: string | null;
          sentVia: string | null;
          sentBy: {
            id: number;
            name: string;
            email: string;
          } | null;
          createdAt: string;
          updatedAt: string;
        }[];
        meta: {
          total: number;
          page: number;
          limit: number;
        };
      },
      {
        fileId: number;
        customerId: number;
        page?: number;
        limit?: number;
        sortBy?: string;
      }
    >({
      query: ({ fileId, customerId, page, limit, sortBy }) => ({
        url: `/payment-reminder/files/${fileId}/customers/${customerId}/mail-logs${parseQueryParams(
          {
            page,
            limit,
            sortBy,
          },
        )}`,
        method: 'GET',
      }),
      providesTags: (result, error, { fileId, customerId }) => [
        { type: 'PaymentReminderMailLogs', id: `${fileId}-${customerId}` },
      ],
    }),

    // Create Customer Remark
    createCustomerRemark: builder.mutation<
      BackendResponse<{
        id: number;
        customerId: number;
        remarks: string;
        createdAt: string;
      }>,
      { fileId: number; customerId: number; remarks: string }
    >({
      query: ({ fileId, customerId, remarks }) => ({
        url: `/payment-reminder/files/${fileId}/customers/${customerId}/remarks`,
        method: 'POST',
        body: { remarks },
      }),
      invalidatesTags: (result, error, { fileId, customerId }) => [
        { type: 'PaymentReminderRemarks', id: `${fileId}-${customerId}` },
      ],
    }),

    // Get Customer Remarks
    getCustomerRemarks: builder.query<
      BackendResponse<
        {
          id: number;
          customerId: number;
          remarks: string;
          createdAt: string;
          createdById: number | null;
          updatedById: number | null;
          deletedById: number | null;
          createdByUser: any | null;
          updatedByUser: any | null;
        }[]
      >,
      { fileId: number; customerId: number; sortBy?: string }
    >({
      query: ({ fileId, customerId, sortBy }) => ({
        url: `/payment-reminder/files/${fileId}/customers/${customerId}/remarks${parseQueryParams(
          {
            sortBy,
          },
        )}`,
        method: 'GET',
      }),
      providesTags: (result, error, { fileId, customerId }) => [
        { type: 'PaymentReminderRemarks', id: `${fileId}-${customerId}` },
      ],
    }),

    // Update Customer Remark
    updateCustomerRemark: builder.mutation<
      BackendResponse<{
        id: number;
        customerId: number;
        remarks: string;
        createdAt: string;
      }>,
      { id: number; remarks: string }
    >({
      query: ({ id, remarks }) => ({
        url: `/payment-reminder/customer-remarks/${id}`,
        method: 'PUT',
        body: { remarks },
      }),
      invalidatesTags: ['PaymentReminderRemarks'],
    }),

    // Delete Customer Remark
    deleteCustomerRemark: builder.mutation<
      BackendResponse<{ message: string }>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/payment-reminder/customer-remarks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentReminderRemarks'],
    }),

    // Download Template
    downloadTemplate: builder.query<Blob, void>({
      query: () => ({
        url: '/payment-reminder/template-download',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Get File Preview (download uploaded file)
    getFilePreview: builder.query<Blob, { fileId: number }>({
      query: ({ fileId }) => ({
        url: `/payment-reminder/files/${fileId}/preview`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Export Customer Aging Summary
    exportAgingSummary: builder.query<Blob, { fileId: number }>({
      query: ({ fileId }) => ({
        url: `/payment-reminder/files/${fileId}/aging-summary/export`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Update Payment Reminder File
    updatePaymentReminderFile: builder.mutation<
      BackendResponse<{
        fileId: number;
        requestNo: string;
        fileName: string;
        fileType: string;
        uploadedAt: string;
        updatedAt: string;
        audit: {
          uploadedBy: {
            id: number;
            name: string;
            email: string;
          } | null;
          updatedBy: {
            id: number;
            name: string;
            email: string;
          } | null;
        };
        stats: {
          totalRecords: number;
          validRecords: number;
          invalidRecords: number;
          totalCustomers: number;
          totalInvoices: number;
        };
      }>,
      { fileId: number; formData: FormData }
    >({
      query: ({ fileId, formData }) => ({
        url: `/payment-reminder/files/${fileId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (result, error, { fileId }) => [
        { type: 'PaymentReminderFiles', id: 'LIST' },
        { type: 'PaymentReminderAgingSummary', id: fileId },
      ],
    }),

    // Delete Payment Reminder File
    deletePaymentReminderFile: builder.mutation<
      BackendResponse<{
        fileId: number;
        requestNo: string;
      }>,
      { fileId: number }
    >({
      query: ({ fileId }) => ({
        url: `/payment-reminder/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentReminderFiles'],
    }),

    // Create Bank Details
    createBankDetails: builder.mutation<
      BackendResponse<{
        id: number;
        companyGroupId: number;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountType: string;
        recipientName: string;
        branchName: string;
        branchAddress: string;
        companyGroup: {
          id: number;
          name: string;
        };
        createdByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        };
      }>,
      {
        companyGroupId: number;
        companyId: number;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountType: string;
        recipientName: string;
        branchName: string;
        branchAddress: string;
      }
    >({
      query: data => ({
        url: '/payment-reminder/bank-details',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PaymentReminderBankDetails'],
    }),

    // Get All Bank Details
    getBankDetails: builder.query<
      {
        id: number;
        companyGroupId: number;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountType: string;
        recipientName: string;
        branchName: string;
        branchAddress: string;
        createdAt: string;
        updatedAt: string | null;
        companyGroup: {
          id: number;
          name: string;
        };
        createdByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
        updatedByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
      }[],
      { companyGroupId?: number }
    >({
      query: ({ companyGroupId }) => ({
        url: `/payment-reminder/bank-details${companyGroupId ? `?companyGroupId=${companyGroupId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['PaymentReminderBankDetails'],
    }),

    // Get Bank Details By ID
    getBankDetailsById: builder.query<
      {
        id: number;
        companyGroupId: number;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountType: string;
        recipientName: string;
        branchName: string;
        branchAddress: string;
        createdAt: string;
        updatedAt: string | null;
        companyGroup: {
          id: number;
          name: string;
        };
        createdByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
        updatedByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/payment-reminder/bank-details/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'PaymentReminderBankDetails', id },
      ],
    }),

    // Update Bank Details
    updateBankDetails: builder.mutation<
      BackendResponse<{
        id: number;
        companyGroupId: number;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountType: string;
        recipientName: string;
        branchName: string;
        branchAddress: string;
        companyGroup: {
          id: number;
          name: string;
        };
        createdByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
        updatedByUser: {
          ID: number;
          FullName: string;
          EmailID: string;
        } | null;
      }>,
      {
        id: number;
        companyGroupId?: number;
        companyId?: number;
        bankName?: string;
        accountNumber?: string;
        ifscCode?: string;
        accountType?: string;
        recipientName?: string;
        branchName?: string;
        branchAddress?: string;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/payment-reminder/bank-details/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        'PaymentReminderBankDetails',
        { type: 'PaymentReminderBankDetails', id },
      ],
    }),

    // Delete Bank Details
    deleteBankDetails: builder.mutation<
      BackendResponse<{
        id: number;
      }>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/payment-reminder/bank-details/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentReminderBankDetails'],
    }),

    // Currency Values (exchange rates to INR)
    getCurrencyValues: builder.query<
      BackendResponse<
        {
          id: number;
          currencyCode: string;
          value: number;
          date: string;
          status: number;
          createdById: number | null;
          createdByUser: {
            ID: number;
            FullName: string;
            EmailID: string;
          } | null;
          updatedByUser: {
            ID: number;
            FullName: string;
            EmailID: string;
          } | null;
        }[]
      >,
      { currencyCode?: string }
    >({
      query: ({ currencyCode }) => ({
        url: `/payment-reminder/currency-values${currencyCode ? `?currencyCode=${encodeURIComponent(currencyCode)}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['PaymentReminderCurrencyValues'],
    }),
    createCurrencyValue: builder.mutation<
      BackendResponse<{
        data: { id: number; currencyCode: string; value: number; date: string };
      }>,
      { currencyCode: string; value: number; date: string }
    >({
      query: body => ({
        url: '/payment-reminder/currency-values',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PaymentReminderCurrencyValues'],
    }),
    deleteCurrencyValue: builder.mutation<
      BackendResponse<{ data: { id: number } }>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/payment-reminder/currency-values/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentReminderCurrencyValues'],
    }),
  }),
});

export const {
  useUploadPaymentReminderFileMutation,
  useGetPaymentReminderFilesQuery,
  useGetPaymentReminderAgingSummaryQuery,
  useGetCustomerInvoicesQuery,
  useGetAllInvoicesQuery,
  useGetCustomersForDropdownQuery,
  useSendPaymentReminderEmailMutation,
  useBulkSendPaymentReminderEmailMutation,
  useGetMailLogsQuery,
  useCreateCustomerRemarkMutation,
  useGetCustomerRemarksQuery,
  useUpdateCustomerRemarkMutation,
  useDeleteCustomerRemarkMutation,
  useLazyDownloadTemplateQuery,
  useLazyGetFilePreviewQuery,
  useLazyExportAgingSummaryQuery,
  useUpdatePaymentReminderFileMutation,
  useDeletePaymentReminderFileMutation,
  useGetBucketInvoiceDetailsQuery,
  useCreateBankDetailsMutation,
  useGetBankDetailsQuery,
  useGetBankDetailsByIdQuery,
  useUpdateBankDetailsMutation,
  useDeleteBankDetailsMutation,
  useGetCurrencyValuesQuery,
  useCreateCurrencyValueMutation,
  useDeleteCurrencyValueMutation,
} = paymentReminderApi;

export default paymentReminderApi;
